import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Skeleton } from "../../feedback/Skeleton";

export type BusinessInfoDisplayCardSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  rows?: number;
};

const DEFAULT_BUSINESS_INFO_SKELETON_ROWS = 6;

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim
 * from Card.tsx / the frozen baseline). */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

const BusinessCardSkeletonRoot = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-root"
})({
  ...fadedCardBorder,
  width: "100%",
  maxWidth: "354px",
  overflow: "hidden",
  borderBottomRightRadius: "10px",
  borderBottomLeftRadius: "10px",
  borderTopLeftRadius: "var(--bfrs-radius-xl)",
  borderTopRightRadius: "var(--bfrs-radius-xl)",
  backgroundImage: "var(--bfrs-gradient-business-info-card)",
  "--tw-shadow": "var(--bfrs-shadow-card)",
  "--tw-shadow-colored": "var(--bfrs-shadow-card)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const BusinessCardSkeletonHeader = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-header"
})({
  display: "flex",
  minHeight: "174px",
  flexDirection: "column",
  alignItems: "center",
  paddingLeft: "1.5rem",
  paddingRight: "1.5rem",
  paddingTop: "25px",
  backgroundImage: "var(--bfrs-gradient-business-card-header)"
});

/* `&&` overrides beat Skeleton's own variant rules, reproducing the old
 * tailwind-merge resolution of the className overrides. */

const BusinessCardSkeletonAvatar = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCardSkeleton-avatar"
})({
  "&&": {
    marginBottom: "8px",
    width: "37px",
    height: "37px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardSkeletonName = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCardSkeleton-name"
})({
  "&&": {
    height: "1.25rem",
    width: "8rem",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardSkeletonSubtitle = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCardSkeleton-subtitle"
})({
  "&&": {
    marginTop: ".25rem",
    height: "1rem",
    width: "10rem",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
});

const BusinessCardSkeletonChipsRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-chipsRow"
})({
  marginTop: "18px",
  display: "flex",
  gap: ".5rem"
});

const BusinessCardSkeletonChip = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCardSkeleton-chip",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { width: string } }>(({ ownerState }) => ({
  "&&": {
    height: "1.75rem",
    width: ownerState.width,
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  }
}));

const BusinessCardSkeletonBody = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-body"
})({
  paddingLeft: "44px",
  paddingRight: "44px",
  paddingBottom: "2rem",
  paddingTop: "8px"
});

const BusinessCardSkeletonGrid = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-grid"
})({
  display: "grid",
  gap: "14px"
});

const BusinessCardSkeletonRow = styled("div", {
  label: "AgenticBusinessInfoDisplayCardSkeleton-row"
})({
  display: "grid",
  gridTemplateColumns: "minmax(0,92px) auto minmax(0,1fr)",
  alignItems: "center",
  MozColumnGap: "1.5rem",
  columnGap: "1.5rem"
});

const BusinessCardSkeletonLine = styled(Skeleton, {
  label: "AgenticBusinessInfoDisplayCardSkeleton-line",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { width: string } }>(({ ownerState }) => ({
  "&&": {
    height: "1rem",
    width: ownerState.width
  }
}));

export const BusinessInfoDisplayCardSkeleton = forwardRef<HTMLDivElement, BusinessInfoDisplayCardSkeletonProps>(
  ({ className, rows = DEFAULT_BUSINESS_INFO_SKELETON_ROWS, ...props }, ref) => (
    <BusinessCardSkeletonRoot ref={ref} className={className} aria-hidden="true" {...props}>
      <BusinessCardSkeletonHeader>
        <BusinessCardSkeletonAvatar animated={false} variant="avatar" />
        <BusinessCardSkeletonName animated={false} variant="text" />
        <BusinessCardSkeletonSubtitle animated={false} variant="text" />
        <BusinessCardSkeletonChipsRow>
          <BusinessCardSkeletonChip animated={false} variant="text" ownerState={{ width: "118px" }} />
          <BusinessCardSkeletonChip animated={false} variant="text" ownerState={{ width: "98px" }} />
        </BusinessCardSkeletonChipsRow>
      </BusinessCardSkeletonHeader>
      <BusinessCardSkeletonBody>
        <BusinessCardSkeletonGrid>
          {Array.from({ length: rows }, (_, index) => (
            <BusinessCardSkeletonRow key={index}>
              <BusinessCardSkeletonLine animated={false} variant="text" ownerState={{ width: "100%" }} />
              <BusinessCardSkeletonLine animated={false} variant="text" ownerState={{ width: ".25rem" }} />
              <BusinessCardSkeletonLine animated={false} variant="text" ownerState={{ width: "6rem" }} />
            </BusinessCardSkeletonRow>
          ))}
        </BusinessCardSkeletonGrid>
      </BusinessCardSkeletonBody>
    </BusinessCardSkeletonRoot>
  )
);

BusinessInfoDisplayCardSkeleton.displayName = "BusinessInfoDisplayCardSkeleton";
